import { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorefrontIcon from '@mui/icons-material/Storefront';

import { fetchOverrideProgress } from './staging';
import type { OverrideProgress } from './staging';

const POLL_INTERVAL_MS = 5_000;

type StepStatus = 'pending' | 'running' | 'completed';

interface Step {
  label: string;
  description: string;
  status: StepStatus;
  current: number;
  total: number;
}

const deriveSteps = (p: OverrideProgress): Step[] => {
  const pricingDone = p.pricingJobTotal > 0 && p.pricingJobSynced >= p.pricingJobTotal;
  const shopifyDone = p.shopifySynced >= p.shopifyTotal;

  return [
    {
      label: 'Upload Progress',
      description: 'Apply override prices to catalog items',
      status: pricingDone ? 'completed' : p.pricingJobTotal > 0 ? 'running' : 'pending',
      current: p.pricingJobSynced,
      total: p.pricingJobTotal,
    },
    {
      label: 'Shopify Sync',
      description: 'Publish updated prices to storefront',
      status: shopifyDone && pricingDone ? 'completed' : p.shopifyTotal > 0 ? 'running' : 'pending',
      current: p.shopifySynced,
      total: p.shopifyTotal,
    },
  ];
};

const getOverallProgress = (steps: Step[]): number => {
  const active = steps.filter(s => s.status !== 'pending' || s.total > 0);
  if (active.length === 0) return 0;

  const perStep = 100 / active.length;
  let progress = 0;
  for (const step of active) {
    if (step.status === 'completed') {
      progress += perStep;
    } else if (step.total > 0) {
      progress += perStep * (step.current / step.total);
    }
  }
  return Math.round(progress);
};

const StepCard = ({ step, icon }: { step: Step; icon: React.ReactNode }) => {
  const percent = step.total > 0 ? Math.round((step.current / step.total) * 100) : 0;

  const chipColor = step.status === 'completed' ? 'success' as const
    : step.status === 'running' ? 'primary' as const
    : 'default' as const;

  const statusLabel = step.status.charAt(0).toUpperCase() + step.status.slice(1);

  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        borderColor: step.status === 'running' ? 'primary.main' : undefined,
        borderWidth: step.status === 'running' ? 2 : 1,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Typography variant="subtitle2" fontWeight="bold" noWrap>
              {step.label}
            </Typography>
          </Box>
          {step.status === 'running' || step.status === 'completed' ? (
            <Chip label={statusLabel} color={chipColor} size="small" />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {statusLabel}
            </Typography>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {step.description}
        </Typography>

        {step.status === 'pending' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Waiting to start
            </Typography>
            <Typography variant="body2" color="text.secondary">
              0%
            </Typography>
          </Box>
        ) : (
          <>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={step.status === 'completed' ? 'success' : 'primary'}
              sx={{ mb: 1, height: 6, borderRadius: 3 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                {step.current.toLocaleString()}
                {' / '}
                {step.total.toLocaleString()}
                {' rows'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {percent}
                {'%'}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const OverrideProgressBanner = () => {
  const [progress, setProgress] = useState<OverrideProgress | null>(null);
  const [error, setError] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const poll = useCallback(async () => {
    try {
      const result = await fetchOverrideProgress();
      setProgress(result);
      setError(false);

      // Keep polling while there's active work
      if (result) {
        timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    poll();
    return () => clearTimeout(timeoutRef.current);
  }, [poll]);

  if (error || !progress) return null;

  const steps = deriveSteps(progress);
  const overallProgress = getOverallProgress(steps);
  const allDone = steps.every(s => s.status === 'completed');

  return (
    <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 3, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Override Upload Progress
        </Typography>
        <Chip
          label={allDone ? 'Completed' : 'Processing'}
          color={allDone ? 'success' : 'primary'}
          size="small"
        />
      </Box>
      {progress.location && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {progress.location}
          {' \u00b7 '}
          {progress.totalRows}
          {' rows'}
        </Typography>
      )}

      {/* Overall progress */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Overall progress</Typography>
          <Typography variant="subtitle2">
            {overallProgress}
            {'%'}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={overallProgress}
          color={allDone ? 'success' : 'primary'}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Step cards */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <StepCard step={steps[0]} icon={<CloudUploadIcon color="primary" />} />
        <StepCard step={steps[1]} icon={<StorefrontIcon color="action" />} />
      </Box>
    </Box>
  );
};

export default OverrideProgressBanner;
